<template>
  <div
    class="bc-tooltip-wrapper"
    @mouseenter="mouseEnterLogo"
    @mouseleave="mouseLeaveLogo"
  >
    <div
      v-on-click-outside="handleOutsideClick"
      class="bc-tooltip"
    >
      <div
        ref="activatorArea"
        class="activator-area"
        @click="handleActivatorClick"
      >
        <slot
          :is-show="isShowRef"
          name="activator"
        />
      </div>
      <template v-if="isShowRef">
        <transition name="fade">
          <teleport :to="teleportId">
            <div
              ref="tooltipArea"
              :style="tooltipStyle"
              class="tooltip-area"
              @mouseenter="mouseEnterLogo"
              @mouseleave="mouseLeaveLogo"
            >
              <slot />
            </div>
          </teleport>
        </transition>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { vOnClickOutside } from '@vueuse/components'
import { computed, reactive, ref, toRef, watch } from 'vue'

import { useBrowserResize } from '../../composables/useBrowserResize'
import { useScroll } from '../../composables/useScroll'
import { useTeleport } from '../../composables/useTeleport'
import { useVModelInterface } from '../../composables/useVModelInterface'
import { parseDomSizeValue } from '../../utils/string.utils'
import type { IBcTooltipWrapperProps } from './tooltip.type'

const props = withDefaults(defineProps<IBcTooltipWrapperProps>(), {
  isShow: null,
  leftMargin: 0,
  zIndex: 1000,
  columnMargin: 8,
  tooltipPosition: 'left',
  triggerType: 'mouseover',
  tooltipWidth: 'initial',
})
const emits = defineEmits([ 'update:isShow' ])
const { isMn, setResizeHandler } = useBrowserResize()
const { setScrollHandler } = useScroll({
  targetElement: props.target,
})
const { removeTeleportEl, createTeleportEl } = useTeleport()

const activatorArea = ref<HTMLDivElement | null>(null)
const tooltipArea = ref<HTMLDivElement | null>(null)
const nowTeleportId = ref<string>('')
const teleportId = computed(() => '#' + nowTeleportId.value)
const tooltipWidth = computed(() => props.width)
const tooltipAreaTranslate = computed(
  () => `translate3d(${props.leftMargin}px, 0, 0)`,
)
let timeoutHandler:any = null
const columnPadding = computed(() => parseDomSizeValue(props.columnMargin))
const columnMargin = computed(() => parseDomSizeValue(-props.columnMargin))

const { value } = useVModelInterface<boolean>(toRef(props, 'isShow'), v =>
  emits('update:isShow', v),
)

const isShowRef = props.isShow === null ? ref(false) : value

const tooltipStyle = reactive({
  top: 'initial',
  left: 'initial',
  right: 'initial',
  width: props.tooltipWidth,
})

const zIndex = computed(() => props.zIndex)

const isMouseOver = computed(() => {
  // 모바일 상태에서 triggerType에 관계 없이 click이 동작해야함 (모바일에서는 마우스 오버가 없기 때문)
  if (isMn.value) {
    return false
  }
  return props.triggerType === 'mouseover'
})

const mouseEnterLogo = () => {
  if (!isMouseOver.value) return
  isShowRef.value = true
}

const mouseLeaveLogo = () => {
  if (!isMouseOver.value) return
  isShowRef.value = false
}

const setTooltipPosition = () => {
  if (!activatorArea.value || !tooltipArea.value) return

  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight
  const activatorBounds = activatorArea.value.getBoundingClientRect()
  const activatorTop = activatorBounds.top
  const activatorHeight = activatorBounds.height
  const activatorLeft = activatorBounds.left
  const activatorRight = activatorBounds.right
  const tooltipWidth = tooltipArea.value.offsetWidth
  const tooltipHeight = tooltipArea.value.offsetHeight
  const isOverflowTooltip = tooltipWidth > windowWidth
  
  if (activatorTop > windowHeight * 0.8) {
    tooltipStyle.top = `${activatorTop - tooltipHeight}px`
  } else {
    tooltipStyle.top = `${activatorTop + activatorHeight}px`
  }
  
  tooltipStyle.left = `${activatorLeft}px`
  tooltipStyle.right = 'auto'

  switch (props.tooltipPosition) {
    case 'left': {
      const left = activatorLeft + props.leftMargin
      tooltipStyle.left = `${left}px`

      if (left + tooltipWidth > windowWidth) {
        tooltipStyle.left = `${windowWidth - tooltipWidth}px`
        // 모바일일 때
        if (isMn.value) {
          const width = isOverflowTooltip ? windowWidth - 16 : tooltipWidth
          tooltipStyle.left = isOverflowTooltip
            ? `${(windowWidth - width) / 2}px`
            : `${windowWidth - tooltipWidth}px`
          tooltipStyle.width = `${width}px`
        }
      }
      break
    }
    case 'right': {
      const leftoverWidth = tooltipWidth - activatorArea.value.offsetWidth
      const right = activatorLeft - leftoverWidth + props.leftMargin
      tooltipStyle.right = `${right}px`

      if (right < 0) {
        tooltipStyle.right = `${-activatorLeft + props.leftMargin}px`
      }
      break
    }
    case 'middle': {
      const mainHalfLeft = activatorLeft + activatorArea.value.offsetWidth / 2
      const tooltipHalfWidth = tooltipWidth / 2
      const mainHalfRight = activatorRight - activatorArea.value.offsetWidth / 2
      const left = mainHalfLeft - tooltipHalfWidth - props.leftMargin
      const right = mainHalfRight - tooltipHalfWidth - props.leftMargin

      if (left < 0) {
        tooltipStyle.left = `${
          -activatorLeft + tooltipHalfWidth + props.leftMargin
        }px`
      } else if (right + tooltipWidth > windowWidth) {
        tooltipStyle.right = `${
          windowWidth - activatorRight - tooltipHalfWidth - props.leftMargin
        }px`
      } else {
        tooltipStyle.left = `${left}px`
      }
      break
    }
  }
}
setResizeHandler(setTooltipPosition)
setScrollHandler(setTooltipPosition)

const handleOutsideClick = () => {
  if (isMouseOver.value) return
  if (props.lockOutsideClick) return
  
  isShowRef.value = false
}

const handleActivatorClick = () => {
  if (isMouseOver.value) return
  if (props.disabled) return
  isShowRef.value = !isShowRef.value
}

watch(tooltipArea, () => {
  setTooltipPosition()
})

watch(isShowRef, () => {
  if (isShowRef.value) {
    nowTeleportId.value = createTeleportEl()

    if (props?.triggerType === 'click' && props?.useAutoClose) {
      timeoutHandler = setTimeout(() => {
        isShowRef.value = false
      }, 1000)
    }
    return
  }
  if (timeoutHandler !== null) {
    clearTimeout(timeoutHandler)
    timeoutHandler = null
  }
  removeTeleportEl(nowTeleportId.value)
})
</script>

<style lang="scss" scoped>
.bc-tooltip-wrapper {
  .bc-tooltip {
    position: relative;
    width: v-bind(tooltipWidth);

    .activator-area {
      width: v-bind(tooltipWidth);
      display: flex;
      align-items: center;
      padding: v-bind(columnPadding) 0;
      margin: v-bind(columnMargin) 0;
    }

    .fade-enter-active,
    .fade-leave-active {
      transition: opacity 0.3s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
      opacity: 0;
    }
  }
}

.tooltip-area {
  z-index: v-bind(zIndex);
  width: max-content;
  position: fixed;
  transform: v-bind(tooltipAreaTranslate);
  border-radius: 8px;
}
</style>
